import { useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const useTier = () => {
  const { api, AdminId } = useGlobalContext();

  const [Loading, setLoading] = useState(false);
  const [benefitsLoading, setBenefitsLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // ======= TIER LIST API =======
  // Original API used Basic token, so use api
  const FetchTierCall = async (driverId = "", setTiers, setFilteredTiers) => {
    setLoading(true);

    try {
      const response = await api.post("/api/Driver/v1.0/getTierList", {
        driverId,
        adminId: AdminId,
      });

      if (response.data?.responseCode === "00") {
        const data = response.data?.data || [];

        toast.success(response.data?.responseMessage || "Tiers loaded");

        setTiers(data);
        setFilteredTiers(data);

        return data;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(response.data?.responseMessage || "Failed to fetch tiers");
      }

      setTiers([]);
      setFilteredTiers([]);

      return [];
    } catch (error) {
      console.error("FetchTierCall error:", error);
      toast.error("Error fetching Tiers");

      setTiers([]);
      setFilteredTiers([]);

      return [];
    } finally {
      setLoading(false);
    }
  };

  // ======= ALL TIER BENEFITS API =======
  // Original API used Basic token, so use api
  const FetchAllTierBenefits = async (setTierBenefitsMap) => {
    setBenefitsLoading(true);

    try {
      const response = await api.post(
        "/api/Driver/v1.0/getAllTierBenefitDetails",
        {
          adminId: AdminId,
        }
      );

      if (response.data?.responseCode === "00") {
        const dataArray = response.data?.data || [];
        const map = {};

        dataArray.forEach((entry) => {
          if (!entry?.tierId) return;
          map[String(entry.tierId)] = entry.benefits || [];
        });

        setTierBenefitsMap(map);

        return map;
      }

      if (shouldShowLocalError(response.data)) {
        toast.error(
          response.data?.responseMessage ||
            "Failed to fetch tier benefits overview"
        );
      }

      setTierBenefitsMap({});

      return {};
    } catch (error) {
      console.error("FetchAllTierBenefits error:", error);
      toast.error("Error fetching tier benefits overview");

      setTierBenefitsMap({});

      return {};
    } finally {
      setBenefitsLoading(false);
    }
  };

  // ===== ADD BENEFIT =====
  // Original API used Basic token, so use api
  const addTierBenefit = async (tierId, benefit, onSuccess) => {
    if (!tierId) {
      toast.error("Invalid tier ID");
      return false;
    }

    if (!benefit?.trim()) {
      toast.error("Benefit is required");
      return false;
    }

    try {
      const res = await api.post("/api/Driver/v1.0/insertTierBenefit", {
        tierId: String(tierId),
        benefit: benefit.trim(),
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Benefit added");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to add benefit");
      }

      return false;
    } catch (error) {
      console.error("addTierBenefit error:", error);
      toast.error("Error adding benefit");

      return false;
    }
  };

  // ===== DELETE BENEFIT =====
  // Original API used Basic token, so use api
  const deleteTierBenefit = async (benefitId, onSuccess) => {
    if (!benefitId) {
      toast.error("Invalid benefit ID");
      return false;
    }

    try {
      const res = await api.post("/api/Driver/v1.0/deleteTierBenefit", {
        benefitId: String(benefitId),
        adminId: AdminId,
      });

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Benefit deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to delete benefit");
      }

      return false;
    } catch (error) {
      console.error("deleteTierBenefit error:", error);
      toast.error("Error deleting benefit");

      return false;
    }
  };

  return {
    FetchTierCall,
    FetchAllTierBenefits,
    addTierBenefit,
    deleteTierBenefit,
    Loading,
    benefitsLoading,
  };
};

export default useTier;